import moment from 'moment'
import React, { useState } from 'react'
import { DatePicker, Dropdown, Menu } from 'antd'
import { Route, Switch, Link } from 'react-router-dom'

import { AiOutlineDown } from 'react-icons/ai'

import { stringifyDate, useListData } from '../../modules'

import Receive from '../../components/reusable/receive'
import CreditNoteDetail from '../../components/drawers/creditNoteDetail'
import ApplyCreditNote from '../../components/drawers/applyCreditNote'

const { RangePicker } = DatePicker

// List spans every supplier (unlike the supplier drawer's own Credit Notes tab), but
// creation still only happens from that drawer's Actions dropdown (Record Credit Note),
// since a note is always tied to a specific supplier and, for returns, a specific
// invoice's line items. View/Apply are still available here via each row's Actions menu.
// Reused from both the Accounts and Procurement nav trees (see index.js in each), so the
// base path for nested routes is resolved from whichever context mounted it.
const CreditNotes = ({ path, namespace, parent }) => {
    const basePath = path ?? `/app/${namespace}/${parent}/credit-notes`

    const [ranges, setRanges] = useState()
    const [string, setString] = useState('')

    const [
        { loading, error, fetch },
        { list: creditNotes },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('creditNotes', ranges ?
        { string, from: ranges[0]?._d ?? null, to: ranges[1]?._d ?? null }
        : { string }
        , { notify: true, manual: false }, 'creditNotes')

    const refetch = () => {
        fetch()
        navigate(1)
    }

    const selectRange = range => {
        setRanges(range)
        refetch()
    }

    const search = str => {
        setString(str)
        if (str?.trim().length > 2 || str.length === 0)
            refetch()
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="flex justify-between items-center mt-1 mb-5">
                <input
                    onChange={e => search(e.target.value)}
                    className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                    id="grid-credit-note-search"
                    type="text"
                    placeholder="Search Credit Notes" />
                <RangePicker
                    onChange={selectRange}
                    ranges={{
                        'Today': [moment().startOf('day'), moment().endOf('day')],
                        'Yesterday': [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')],
                        'This Week': [moment().startOf('week'), moment().endOf('week')],
                        'This Month': [moment().startOf('month'), moment().endOf('month')],
                    }} />
            </div>
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="font-semibold text-gray-800">Credit Notes</h2>
                </header>
                <Receive
                    error={error}
                    loading={loading}
                    progress>
                    {
                        creditNotes.length > 0 ?
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Supplier</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Type</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Invoice</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Reason</div></th>
                                        <th className="p-2"><div className="font-semibold text-center">Amount</div></th>
                                        <th className="p-2"><div className="font-semibold text-center">Unused</div></th>
                                        <th className="p-2"><div className="font-semibold text-left">Date</div></th>
                                        <th className="p-2"><div className="font-semibold text-center"></div></th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        creditNotes.map((note, key) => {
                                            const unused = note.amount - note.amountApplied
                                            return (
                                                <tr key={ note._id }>
                                                    <td className="p-2"><div className="text-center">{((page - 1) * limit) + key + 1}.</div></td>
                                                    <td className="p-2"><div>{ note.supplier?.name }</div></td>
                                                    <td className="p-2"><div className="capitalize">{ note.type }</div></td>
                                                    <td className="p-2"><div>{ note.invoice?.code ?? '—' }</div></td>
                                                    <td className="p-2"><div>{ note.reason }</div></td>
                                                    <td className="p-2"><div className="text-center">Ksh. { note.amount }</div></td>
                                                    <td className="p-2"><div className="text-center">Ksh. { unused }</div></td>
                                                    <td className="p-2"><div>{ stringifyDate(note.date) }</div></td>
                                                    <td className="p-2 text-center">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Link to={`${basePath}/${note._id}`}>
                                                                        <Menu.Item>View</Menu.Item>
                                                                    </Link>
                                                                    {
                                                                        unused > 0 && note.supplier?._id &&
                                                                        <Link to={`${basePath}/${note._id}/apply/${note.supplier._id}`}>
                                                                            <Menu.Item>Apply</Menu.Item>
                                                                        </Link>
                                                                    }
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No credit notes have been recorded yet</p>
                            </div>
                    }
                </Receive>
                {Paginate}
            </div>
            <Switch>
                <Route path={ `${basePath}/:noteId/apply/:supplierId` }>
                    <ApplyCreditNote onPerform={ refetch } />
                </Route>
                <Route path={ `${basePath}/:noteId` }>
                    <CreditNoteDetail />
                </Route>
            </Switch>
        </div>
    )
}

export default CreditNotes
